import { Injectable } from '@angular/core';
import {
  HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest
} from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * SD-1218: catches the 403 `{ error: "module_not_entitled", module: "<code>" }` emitted by the
 * server's RequireModule filter and broadcasts the module code.
 *
 * Two subscribers matter: EntitlementsService refreshes (a client holding a stale set
 * self-heals rather than continuing to show an item it has lost), and the content layout
 * shows the user why the page it just tried to open is unavailable.
 *
 * The static Subject is copied from EmailConfirmedInterceptor, where it avoids a circular DI
 * dependency between the interceptor and its consumer. Kept as a separate interceptor rather
 * than a second branch in that one: they are unrelated concerns that happen to share a
 * status code.
 */
@Injectable({ providedIn: 'root' })
export class ModuleEntitlementInterceptor implements HttpInterceptor {

  /** Emits the module code that was refused. */
  static readonly moduleNotEntitled$ = new Subject<string>();

  /**
   * SD-1229: a refusal a plan upgrade would fix -- a module or feature some plan on sale
   * includes (`upgradable: true`), or a plan limit reached (`limit_exceeded`). PlanPromptService
   * answers it with the upgrade modal.
   */
  static readonly upgradeSuggested$ = new Subject<{ kind: 'module' | 'limit'; code: string }>();

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 403) {
          const body = error.error;
          const parsed = typeof body === 'string' ? this.safeParse(body) : body;

          if (parsed && parsed.error === 'module_not_entitled') {
            ModuleEntitlementInterceptor.moduleNotEntitled$.next(parsed.module || '');
            if (parsed.upgradable === true) {
              ModuleEntitlementInterceptor.upgradeSuggested$.next({ kind: 'module', code: parsed.module || '' });
            }
          }

          if (parsed && parsed.error === 'limit_exceeded' && parsed.upgradable === true) {
            ModuleEntitlementInterceptor.upgradeSuggested$.next({ kind: 'limit', code: parsed.limit || '' });
          }
        }
        return throwError(error);
      })
    );
  }

  private safeParse(s: string): any {
    try { return JSON.parse(s); } catch { return null; }
  }
}
